import SharedLayout from "../components/SharedLayout";
import useSEO from "../utils/useSEO";

const LAST_UPDATED = "May 22, 2026";
const EFFECTIVE_DATE = "May 22, 2026";
const COMPANY = "BerryByte Innovations";
const WEBSITE = "https://www.berrybyteinn.com";
const EMAIL = "sales@berrybyteinn.com";
const PHONE = "+1 201-846-9830";
const STATE = "Texas";

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10">
    <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
      <span className="w-1 h-6 rounded-full bg-gradient-to-b from-violet-500 to-cyan-500 flex-shrink-0" />
      {title}
    </h2>
    <div className="text-slate-400 leading-relaxed space-y-3 pl-4">{children}</div>
  </section>
);

const P = ({ children }) => <p className="text-slate-400 leading-relaxed">{children}</p>;

const UL = ({ items }) => (
  <ul className="space-y-2 pl-2">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2 text-slate-400">
        <span className="w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0 mt-2" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Note = ({ children }) => (
  <div className="my-4 p-4 rounded-xl border border-amber-500/20 bg-amber-500/5">
    <p className="text-amber-300 text-sm leading-relaxed">{children}</p>
  </div>
);

const TOC = [
  { id: "acceptance", label: "Acceptance of Terms" },
  { id: "services", label: "Services Description" },
  { id: "client-obligations", label: "Client Obligations" },
  { id: "project-engagements", label: "Project Engagements & Proposals" },
  { id: "payment", label: "Payment Terms" },
  { id: "ip", label: "Intellectual Property" },
  { id: "confidentiality", label: "Confidentiality & NDA" },
  { id: "communications", label: "Communications & Tools" },
  { id: "warranties", label: "Warranties & Representations" },
  { id: "liability", label: "Limitation of Liability" },
  { id: "indemnification", label: "Indemnification" },
  { id: "termination", label: "Termination" },
  { id: "governing-law", label: "Governing Law & Disputes" },
  { id: "changes", label: "Changes to These Terms" },
  { id: "contact", label: "Contact Information" },
];

export default function TermsConditions() {
  useSEO({
    title: "Terms & Conditions | BerryByte Innovations",
    description: "Terms and Conditions for BerryByte Innovations software development services. Governing law: Texas. Last updated May 2026.",
    canonical: "https://www.berrybyteinn.com/terms",
  });

  return (
    <SharedLayout>
      <div className="relative pt-32 pb-20 bg-[#050810]">
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8">

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300 text-sm font-medium mb-6">
              Legal Document
            </div>
            <h1 className="text-4xl lg:text-5xl font-black text-white mb-4">Terms &amp; Conditions</h1>
            <div className="flex flex-wrap gap-6 text-sm text-slate-500">
              <span>Effective Date: {EFFECTIVE_DATE}</span>
              <span>Last Updated: {LAST_UPDATED}</span>
            </div>
            <div className="mt-6 p-4 rounded-xl border border-white/8 bg-white/[0.02]">
              <P>
                These Terms and Conditions ("Terms") govern your use of the website located at {WEBSITE}
                and your engagement with the services provided by {COMPANY} ("Company," "we," "us," or "our"),
                a software development agency based in {STATE}, United States. By accessing our website,
                submitting an inquiry, or entering into a service agreement with us, you agree to be bound
                by these Terms. If you do not agree, please do not use our website or services.
              </P>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="mb-12 p-6 rounded-2xl border border-white/8 bg-white/[0.02]">
            <h2 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Table of Contents</h2>
            <div className="grid sm:grid-cols-2 gap-2">
              {TOC.map(({ id, label }, i) => (
                <a key={id} href={`#${id}`} className="flex items-center gap-2 text-sm text-slate-400 hover:text-violet-400 transition-colors py-1">
                  <span className="text-violet-500/60 font-mono text-xs w-5">{String(i + 1).padStart(2, "0")}.</span>
                  {label}
                </a>
              ))}
            </div>
          </div>

          {/* Sections */}
          <Section id="acceptance" title="1. Acceptance of Terms">
            <P>
              By accessing or using the {COMPANY} website, submitting a contact form, requesting a proposal,
              or entering into any service agreement with us, you confirm that you have read, understood,
              and agree to these Terms and our Privacy Policy, which is incorporated herein by reference.
            </P>
            <P>
              If you are accepting these Terms on behalf of a company or other legal entity, you represent
              that you have the authority to bind that entity to these Terms. These Terms apply to all
              visitors, prospective clients, and current clients.
            </P>
          </Section>

          <Section id="services" title="2. Services Description">
            <P>{COMPANY} provides professional technology services including, but not limited to:</P>
            <UL items={[
              "Custom web application development (React, Next.js, TypeScript, and related technologies)",
              "Mobile application development (iOS, Android, React Native, Flutter)",
              "Backend engineering (Node.js, Python, Go, REST APIs, GraphQL, databases)",
              "Cloud solutions and DevOps (AWS, GCP, Azure, Terraform, Kubernetes, CI/CD)",
              "UI/UX design and product design services",
              "AI integration, LLM development, and workflow automation",
              "BerryChat CRM platform — customer support, live chat, and CRM software",
              "Technical consulting, architecture review, and advisory services",
            ]} />
            <P>
              The specific scope of services for each engagement is defined in a written Statement of Work
              (SOW), proposal, or service agreement agreed upon by both parties. These Terms apply to all
              engagements unless superseded by a separate written agreement.
            </P>
          </Section>

          <Section id="client-obligations" title="3. Client Obligations">
            <P>To enable us to deliver services effectively, clients agree to:</P>
            <UL items={[
              "Provide accurate, complete, and timely information, materials, and feedback required for project delivery",
              "Designate a primary point of contact with authority to make project decisions",
              "Respond to requests for approvals, feedback, or decisions within the timeframes specified in project agreements",
              "Ensure that all content, assets, and materials provided to us do not infringe the intellectual property rights of any third party",
              "Maintain the confidentiality of project-related information and access credentials",
              "Make payments according to the agreed schedule",
              "Comply with all applicable laws in connection with your use of our services and any products we develop for you",
            ]} />
            <P>Delays in providing required information or approvals may result in project timeline adjustments and potential additional costs, which we will communicate in writing.</P>
          </Section>

          <Section id="project-engagements" title="4. Project Engagements & Proposals">
            <P>
              All project engagements begin with a written proposal or Statement of Work (SOW) that defines
              the scope, deliverables, timeline, pricing, and payment schedule. A project is formally
              initiated upon:
            </P>
            <UL items={[
              "Written acceptance of the proposal or SOW by both parties (email confirmation is acceptable)",
              "Receipt of any required upfront payment or deposit as specified in the proposal",
            ]} />
            <P>
              Proposals are valid for 30 days from the date of issue unless otherwise specified.
              Any changes to the agreed scope after project initiation must be submitted in writing and
              will be assessed for schedule and cost impact through a formal Change Order process.
            </P>
            <Note>
              Verbal agreements, informal communications, or social media messages do not constitute
              binding project commitments. All agreements must be documented in writing.
            </Note>
          </Section>

          <Section id="payment" title="5. Payment Terms">
            <P>Payment terms are specified in each project proposal or service agreement. Standard terms include:</P>
            <UL items={[
              "A deposit (typically 30–50% of project value) is due upon project initiation before work commences",
              "Milestone-based payments are due upon completion of agreed project milestones",
              "Final payment is due upon project delivery or as specified in the agreement",
              "Ongoing retainer fees are billed monthly in advance",
              "All prices are in USD unless otherwise specified",
            ]} />
            <p className="font-semibold text-slate-300 mt-4">Late Payments</p>
            <P>
              Invoices are due within 15 days of issuance unless otherwise agreed. Overdue invoices may
              accrue interest at 1.5% per month (18% per annum) or the maximum rate permitted by law,
              whichever is lower. We reserve the right to pause or suspend work on projects with
              outstanding overdue balances after providing written notice.
            </P>
            <p className="font-semibold text-slate-300 mt-4">Refunds</p>
            <P>
              Deposits are non-refundable once work has commenced. For milestone-based projects, payments
              made for completed milestones are non-refundable. If you cancel a project after initiation,
              you remain responsible for payment for all work completed up to the cancellation date.
              Refund requests for exceptional circumstances are considered on a case-by-case basis.
            </P>
          </Section>

          <Section id="ip" title="6. Intellectual Property">
            <p className="font-semibold text-slate-300">Client-Owned Deliverables</p>
            <P>
              Upon receipt of full payment for a project, {COMPANY} assigns to the client all intellectual
              property rights in the custom deliverables created specifically for that client, including
              custom source code, designs, and documentation, except as noted below.
            </P>
            <p className="font-semibold text-slate-300 mt-4">Retained Rights</p>
            <P>{COMPANY} retains ownership of:</P>
            <UL items={[
              "Pre-existing tools, frameworks, libraries, methodologies, and know-how that we bring to the engagement",
              "Open-source components (which are subject to their respective licenses)",
              "Generalized code patterns, utilities, and components that are not specific to the client's product",
              "Our internal processes, documentation templates, and development frameworks",
            ]} />
            <P>
              We grant clients a non-exclusive, perpetual license to use any retained components that
              are incorporated into their deliverables.
            </P>
            <p className="font-semibold text-slate-300 mt-4">Client Materials</p>
            <P>
              You represent that you own or have the right to use all materials, content, trademarks,
              and assets you provide to us. You grant us a limited license to use such materials solely
              to perform our services.
            </P>
            <p className="font-semibold text-slate-300 mt-4">Portfolio Rights</p>
            <P>
              Unless you request otherwise in writing, we reserve the right to reference your company
              name, project type, and general outcomes in our portfolio, case studies, and marketing
              materials. We will not disclose confidential project details without your permission.
            </P>
          </Section>

          <Section id="confidentiality" title="7. Confidentiality & NDA">
            <P>
              Both parties agree to keep confidential all non-public information shared during the
              engagement, including business plans, technical specifications, financial data, and
              strategic information ("Confidential Information").
            </P>
            <UL items={[
              "Confidential Information will be used only for the purpose of delivering or receiving services",
              "Each party will protect the other's Confidential Information with at least the same care used for its own confidential information (no less than reasonable care)",
              "Confidentiality obligations do not apply to information that is publicly available, independently developed, or required to be disclosed by law",
              "Upon request or project completion, each party will return or destroy the other's Confidential Information",
            ]} />
            <P>
              Where clients require a formal Non-Disclosure Agreement (NDA) prior to initial discussions,
              we are pleased to sign a mutual NDA. Confidential information shared is protected regardless
              of whether a separate NDA is in place, as these Terms establish confidentiality obligations.
            </P>
          </Section>

          <Section id="communications" title="8. Communications & Tools">
            <P>
              By engaging our services or submitting your contact information, you consent to receive
              communications from {COMPANY} through the following channels related to your inquiry,
              project, or our services:
            </P>
            <UL items={[
              "Email: Primary channel for proposals, project updates, invoices, and documentation",
              "Zoom Video & Zoom Phone: Used for project meetings, discovery calls, and client communications. Sessions may be recorded with prior consent for project documentation",
              "SMS / Text Messages: Used for appointment reminders and time-sensitive project updates where you have provided a mobile number and consented to SMS contact",
              "BerryChat Live Chat: Used for real-time support and inquiry responses on our website",
              "Phone: Direct voice calls at the number you provide",
            ]} />
            <P>
              You may opt out of non-essential communications at any time by contacting us at {EMAIL}.
              Service-related communications (invoices, project updates, legal notices) cannot be opted
              out of during an active engagement.
            </P>
          </Section>

          <Section id="warranties" title="9. Warranties & Representations">
            <p className="font-semibold text-slate-300">Our Warranties</p>
            <P>{COMPANY} warrants that:</P>
            <UL items={[
              "Services will be performed by qualified professionals in a workmanlike manner consistent with industry standards",
              "We have the right to enter into service agreements and deliver the services described",
              "Deliverables will materially conform to the agreed specifications at the time of delivery",
              "We will promptly address and correct material defects in deliverables reported within 30 days of delivery",
            ]} />
            <p className="font-semibold text-slate-300 mt-4">Disclaimer</p>
            <P>
              EXCEPT AS EXPRESSLY STATED ABOVE, OUR SERVICES AND DELIVERABLES ARE PROVIDED "AS IS"
              WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES
              OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT
              WARRANT THAT DELIVERABLES WILL BE ERROR-FREE OR OPERATE WITHOUT INTERRUPTION.
            </P>
          </Section>

          <Section id="liability" title="10. Limitation of Liability">
            <P>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, {COMPANY.toUpperCase()}'S TOTAL LIABILITY
              TO YOU FOR ANY CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR OUR SERVICES SHALL NOT
              EXCEED THE TOTAL FEES PAID BY YOU TO US IN THE THREE (3) MONTHS IMMEDIATELY PRECEDING THE
              EVENT GIVING RISE TO THE CLAIM.
            </P>
            <P>
              IN NO EVENT SHALL {COMPANY.toUpperCase()} BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, LOSS OF DATA, LOSS OF
              BUSINESS OPPORTUNITY, OR REPUTATIONAL DAMAGE, EVEN IF WE HAVE BEEN ADVISED OF THE
              POSSIBILITY OF SUCH DAMAGES.
            </P>
            <Note>
              Some jurisdictions do not allow the exclusion of certain warranties or limitation of
              liability for consequential damages. In such cases, our liability is limited to the
              fullest extent permitted by applicable law.
            </Note>
          </Section>

          <Section id="indemnification" title="11. Indemnification">
            <P>
              You agree to indemnify, defend, and hold harmless {COMPANY} and its officers, directors,
              employees, and contractors from and against any claims, damages, losses, liabilities, costs,
              and expenses (including reasonable legal fees) arising from:
            </P>
            <UL items={[
              "Your breach of these Terms",
              "Your violation of any applicable law or third-party rights",
              "Content, materials, or instructions you provide to us that infringe third-party intellectual property rights",
              "Your use of the deliverables we provide in a manner not contemplated by these Terms",
              "Any claim by a third party related to products or services that incorporate our deliverables",
            ]} />
          </Section>

          <Section id="termination" title="12. Termination">
            <p className="font-semibold text-slate-300">Termination by Client</p>
            <P>
              You may terminate a project engagement by providing written notice to us at {EMAIL}.
              Upon termination, you are responsible for payment for all work completed up to the
              termination date, plus any non-cancellable third-party costs incurred on your behalf.
            </P>
            <p className="font-semibold text-slate-300 mt-4">Termination by {COMPANY}</p>
            <P>We may terminate or suspend services immediately upon written notice if:</P>
            <UL items={[
              "You fail to make payment when due and do not cure the failure within 10 business days of written notice",
              "You materially breach these Terms or a project agreement and fail to cure within 15 days of notice",
              "You engage in illegal activity or request work that violates applicable law",
              "Continued performance would require us to violate applicable law or professional standards",
            ]} />
            <p className="font-semibold text-slate-300 mt-4">Effect of Termination</p>
            <P>
              Upon termination, all licenses granted under these Terms terminate, you must pay any
              outstanding invoices, and each party will return or destroy the other's Confidential
              Information. Sections that by their nature should survive termination (including Payment,
              IP, Confidentiality, Liability, and Governing Law) will survive.
            </P>
          </Section>

          <Section id="governing-law" title="13. Governing Law & Disputes">
            <P>
              These Terms are governed by and construed in accordance with the laws of the State of
              {" "}{STATE}, United States, without regard to its conflict of law principles.
            </P>
            <p className="font-semibold text-slate-300 mt-4">Informal Resolution</p>
            <P>
              Before initiating any formal dispute process, both parties agree to attempt to resolve
              disputes informally by contacting {EMAIL}. We will try to resolve disputes within 30 days.
            </P>
            <p className="font-semibold text-slate-300 mt-4">Binding Arbitration</p>
            <P>
              If informal resolution fails, disputes arising out of or relating to these Terms shall be
              resolved by binding arbitration administered under the American Arbitration Association
              (AAA) Commercial Arbitration Rules. Arbitration shall take place in {STATE}. The arbitrator's
              decision shall be final and binding and may be entered as a judgment in any court of
              competent jurisdiction.
            </P>
            <p className="font-semibold text-slate-300 mt-4">Class Action Waiver</p>
            <P>
              You agree that any dispute resolution will be conducted on an individual basis and not
              as a class action, collective action, or representative proceeding.
            </P>
            <p className="font-semibold text-slate-300 mt-4">Exceptions</p>
            <P>
              Either party may seek injunctive or other equitable relief in a court of competent
              jurisdiction to prevent irreparable harm pending arbitration.
            </P>
          </Section>

          <Section id="changes" title="14. Changes to These Terms">
            <P>
              We may update these Terms from time to time. When we make material changes, we will update
              the "Last Updated" date at the top of this page and post the updated Terms on our website.
              For active clients, we will provide 30 days' written notice of material changes.
            </P>
            <P>
              Your continued use of our website or services after the effective date of any changes
              constitutes your acceptance of the updated Terms. If you do not agree with the changes,
              you should discontinue use of our services and provide written notice of termination.
            </P>
          </Section>

          <Section id="contact" title="15. Contact Information">
            <P>
              For questions about these Terms, to exercise any rights, or to report a concern, please
              contact us:
            </P>
            <div className="mt-4 p-5 rounded-xl border border-white/8 bg-white/[0.02] space-y-3">
              <div className="flex items-center gap-3 text-slate-300 font-semibold">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-violet-400 flex-shrink-0">
                  <path d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
                </svg>
                {COMPANY}
              </div>
              <div className="flex items-center gap-3 text-slate-400">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-violet-400 flex-shrink-0">
                  <path d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                </svg>
                <a href={`mailto:${EMAIL}`} className="hover:text-white transition-colors">{EMAIL}</a>
              </div>
              <div className="flex items-center gap-3 text-slate-400">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-violet-400 flex-shrink-0">
                  <path d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25z" />
                </svg>
                <a href="tel:+12018469830" className="hover:text-white transition-colors">{PHONE}</a>
              </div>
              <div className="flex items-center gap-3 text-slate-400">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-violet-400 flex-shrink-0">
                  <path d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" /><path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0z" />
                </svg>
                <span>{STATE}, United States</span>
              </div>
            </div>
          </Section>

          {/* Final note */}
          <div className="mt-10 p-5 rounded-2xl border border-white/8 bg-white/[0.02] text-center">
            <p className="text-slate-500 text-sm">
              By using our website or engaging our services, you acknowledge that you have read,
              understood, and agree to these Terms and Conditions.
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
              <a href="/privacy-policy" className="text-violet-400 hover:text-violet-300 text-sm font-medium transition-colors">
                Privacy Policy
              </a>
              <span className="text-slate-700 hidden sm:block">·</span>
              <a href="/contact" className="text-violet-400 hover:text-violet-300 text-sm font-medium transition-colors">
                Contact Us
              </a>
            </div>
          </div>

        </div>
      </div>
    </SharedLayout>
  );
}
